import { APP_INITIALIZER, NgModule } from '@angular/core'
import { BrowserModule, DomSanitizer, HammerModule } from '@angular/platform-browser'

import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { environment } from '../environments/environment'
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { LoginComponent } from './login/login.component'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { StoreModule } from '@ngrx/store'
import { EffectsModule } from '@ngrx/effects'
import { StoreRouterConnectingModule } from '@ngrx/router-store'
import { NgrxFormsModule } from 'ngrx-forms'
import { NavBarComponent } from './nav-bar/nav-bar.component'
import { RootReducers } from './app.root.state'
import { CustomErrorStateMatcherDirective } from './login/error-state-matcher'
import { ApiModule } from '@api/api.module'
import { AccountModule } from './account/account.module'
import { OrdersModule } from './orders/orders.module'
import { LoginEffects } from './login/login.effects'
import { MatMenuModule } from '@angular/material/menu'
import { MatListModule } from '@angular/material/list'
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatIconModule, MatIconRegistry } from '@angular/material/icon'
import { DashboardComponent } from './dashboard/dashboard.component'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { HttpClient } from '@angular/common/http'
import { MatCardModule } from '@angular/material/card'
import { MatGridListModule } from '@angular/material/grid-list'
import { MatTableModule } from '@angular/material/table'
import { MatRadioModule } from '@angular/material/radio'
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog'
import { MatSidenavModule } from '@angular/material/sidenav'
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular'
// import { MatSelectCountryModule } from '@angular-material-extensions/select-country'
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar'
import { ErrorSnackBarComponent } from './utils/components/error-snack-bar/error-snack-bar.component'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import {DatePipe, NgOptimizedImage} from '@angular/common'
import { InfoSnackBarComponent } from './utils/components/info-snack-bar/info-snack-bar.component'
import { SuccessSnackBarComponent } from './utils/components/success-snack-bar/success-snack-bar.component'
import { CancellationDialogComponent } from './orders/orders-dashboard/orders-table/cancellation-dialog/cancellation-dialog.component'
import { UUIDNotFoundComponent } from './uuidnot-found/uuidnot-found.component'
import { DigitalSubscriptionContentComponent } from './digital-subscription-content/digital-subscription-content.component'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DigitalContentLinksComponent } from './digital-content-links/digital-content-links.component'
import { DigitalContentLinksGuard } from './digital-content-links/digital-content-links-guard.service'
import { DigitalSubscriptionContentGuard } from './digital-subscription-content/digital-subscription-content-guard.service'
import { browserLang, getSupportedLocale, HttpLoaderFactory } from './language'
import {GroupsEffects} from "./groups/store/groups.effects";
import {GroupsService} from "./groups/groups.service";
import { COUNTRIES_DB_DE } from './utils/components/select-country/i18n/de'

function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak.init({
      enableBearerInterceptor: true,
      config: {
        url: environment.authUrl,
        realm: environment.realmId,
        clientId: environment.clientId,
      },
      initOptions: {
        redirectUri: window.location.href,
        checkLoginIframe: false,
      },
    })
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    NavBarComponent,
    CustomErrorStateMatcherDirective,
    ErrorSnackBarComponent,
    InfoSnackBarComponent,
    SuccessSnackBarComponent,
    CancellationDialogComponent,
    UUIDNotFoundComponent,
    DigitalSubscriptionContentComponent,
    DigitalContentLinksComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LoggerModule.forRoot({
      level: environment.production ? NgxLoggerLevel.INFO : NgxLoggerLevel.DEBUG,
      colorScheme: ['grey', 'aqua', 'lime', 'fuchsia', '', '', 'red'],
      enableSourceMaps: !environment.production,
      timestampFormat: 'HH:mm:ss:SSS',
    }),
    BrowserAnimationsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatMenuModule,
    MatListModule,
    MatTableModule,
    MatRadioModule,
    MatToolbarModule,
    MatGridListModule,
    StoreModule.forRoot(RootReducers, {}),
    EffectsModule.forRoot([LoginEffects]),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument({maxAge: 25, logOnly: environment.production, connectInZone: true}) : [],
    NgrxFormsModule,
    ApiModule,
    AccountModule,
    OrdersModule,
    MatIconModule,
    TranslateModule.forRoot({
      defaultLanguage: getSupportedLocale(browserLang),
      isolate: false,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MatCardModule,
    HammerModule,
    MatSidenavModule,
    KeycloakAngularModule,
    // MatSelectCountryModule.forRoot('de'),
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    NgOptimizedImage,
  ],
  providers: [
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { ...new MatDialogConfig(), maxWidth: '464px' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { horizontalPosition: 'end', verticalPosition: 'top', duration: 5000 } as MatSnackBarConfig },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    DatePipe,
    DigitalSubscriptionContentGuard,
    DigitalContentLinksGuard,
    GroupsService
  ],
  bootstrap: [AppComponent],
  exports: [CustomErrorStateMatcherDirective],
})
export class AppModule {
  constructor(
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    this.registerCountries()
  }

  registerCountries() {
    for (const country of COUNTRIES_DB_DE) {
      const countryAlpha2Code = country.alpha2Code.toLowerCase();
      try {
        this.iconRegistry.addSvgIcon(
          countryAlpha2Code,
          this.sanitizer.bypassSecurityTrustResourceUrl(
            `assets/svg-country-flags/svg/${countryAlpha2Code}.svg`
          )
        );
      } catch (err) {
        console.error("Error: icon not found for " + countryAlpha2Code, err);
      }
    }
  }

}
